import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { debugLog } from '../../helpers/debugLog';
import { extractFileNameWithoutExtension } from '../../helpers/locator';
import { quizModeFor } from '../../helpers/quiz';
import { isQuizOnPage, type CurrentPage } from '../../helpers/quiz-placement';
import {
  createAnchorLocator,
  findVisibleIframe,
  getBodyTextForSpeech,
  getVisibleTextFragment,
  paginationGeometry,
} from '../../helpers/readium';
import { isHighlight } from '../../helpers/selections';
import type { TextQuote } from '../../helpers/text';
import type {
  AnnotationMaterialResponse,
  AnnotationMaterialSave,
  QuestionAnswerSave,
  QuestionMaterialResponse,
  QuestionMaterialSave,
  QuizAnswerResponse,
  QuizAnswerSave,
  QuizAnswerValueSave,
  QuizChoice,
  QuizDraft,
  QuizDraftQuestion,
  QuizMaterialResponse,
  QuizMaterialSave,
  QuizQuestionAiResponse,
  QuizSelection,
  QuizWithStatus,
  RawAnswerResponse,
  RawMaterialResponse,
  VoiceAnswerSave,
  VoiceMaterialResponse,
  VoiceMaterialSave,
} from '../../models/quiz';
import type { Highlight } from '../../models/selections';
import { MetadataService } from '../metadata/metadata';
import { ReaderApi } from '../reader-api/reader-api';
import { BookAnalysisService } from '../readium/book-analysis';
import { ReaderNavigatorService } from '../readium/navigator/reader-navigator';
import { SelectionsService } from '../selections/selections';
import { ToastService } from '../toast/toast';

export const DEFAULT_ANSWER_COUNT = 3;
const BYTES_PER_KB = 1024;
const MAX_IMAGE_MB = 5;
const MAX_IMAGE_BYTES = MAX_IMAGE_MB * BYTES_PER_KB * BYTES_PER_KB;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
const MAX_AI_INPUT_LENGTH = 25000;
const SECONDS_PER_MINUTE = 60;

export type AiTextSource = 'visible' | 'chapter';

export type QuizMode = 'question' | 'quiz' | 'note' | 'voice';

export interface GenerateQuizWithAiOptions {
  source: AiTextSource;
  seedText: string;
  questionsNumber: number;
  numberOfAnswers: number;
  competencyIds: string[];
}

export interface GenerateQuizWithAiResult {
  questions: QuizDraftQuestion[];
  partial: boolean;
}

function makeEmptyDraftQuestion(): QuizDraftQuestion {
  const choices: QuizChoice[] = Array.from({ length: DEFAULT_ANSWER_COUNT }, () => ({ text: '' }));
  return { tempId: crypto.randomUUID(), text: '', choices, correctChoice: 0 };
}

@Injectable({
  providedIn: 'root',
})
export class QuizService {
  private readonly readerApi = inject(ReaderApi);
  private readonly metadataService = inject(MetadataService);
  private readonly readerNavigatorService = inject(ReaderNavigatorService);
  private readonly selectionsService = inject(SelectionsService);
  private readonly bookAnalysis = inject(BookAnalysisService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  // Longest prefix of a selection kept as a question's text anchor.
  private static readonly MAX_ANCHOR_LENGTH = 200;

  private readonly bookRelId = computed(
    () => this.metadataService.metadata()?.userConfig?.bookId ?? '',
  );

  // Seeded from the loaded metadata and owned here from then on: MetadataService.metadata must
  // never be written after load, since that rebuilds the EpubNavigator at the open-time position.
  private readonly rawQuizzes = linkedSignal<RawMaterialResponse[]>(
    () => this.metadataService.metadata()?.materials?.questions ?? [],
  );

  private readonly rawAnswers = linkedSignal<RawAnswerResponse[]>(
    () => this.metadataService.metadata()?.materials?.answers ?? [],
  );

  readonly quizzes = computed<QuizWithStatus[]>(() =>
    this.rawQuizzes().map((quiz) => this.enrich(quiz)),
  );

  readonly visibleQuizzes = computed<QuizWithStatus[]>(() => {
    const currentLocator = this.readerNavigatorService.actualLocator();
    if (!currentLocator) return [];

    // The frame's geometry and text are read untracked. They are fresh anyway: Readium sets a new
    // `actualLocator` after every scroll (`reportProgress` -> `positionChanged`), so this computed
    // reruns on each page turn. Do not add an `equal` to `actualLocator`, it would freeze them.
    const frames = this.readerNavigatorService.getCurrentFrameElements();
    const page: CurrentPage = {
      componentIds: this.readerNavigatorService
        .currentResourceHrefs()
        .map((href) => extractFileNameWithoutExtension(href)),
      componentNames: this.metadataService.componentNames(),
      fixedLayout: this.metadataService.isFixedLayout(),
      geometry: paginationGeometry(findVisibleIframe(frames)),
      anchors: createAnchorLocator(frames),
    };
    const highlightsById = new Map(
      this.selectionsService
        .selections()
        .filter(isHighlight)
        .map((highlight) => [highlight.id, highlight]),
    );

    return this.quizzes().filter(({ quiz }) =>
      isQuizOnPage(quiz, highlightsById.get(quiz.selection.id), page),
    );
  });

  readonly quizDraft = signal<QuizDraft | undefined>(undefined);

  readonly quizToAnswer = signal<QuizMaterialResponse | undefined>(undefined);

  readonly aiSeedText = signal<string | undefined>(undefined);

  readonly quizMode = signal<QuizMode>('quiz');

  startCreateQuiz(ai = false, seedText?: string, mode: QuizMode = 'quiz'): void {
    const locator = this.readerNavigatorService.actualLocator();
    if (!locator) return;
    this.quizDraft.set({
      name: '',
      relId: this.bookRelId(),
      selection: {
        id: crypto.randomUUID(),
        componentId: this.metadataService.componentIdFor(locator.href),
        percent: String(locator.locations.progression ?? 0),
        ebookReadingPercentage: this.bookAnalysis.calculateProgressFromLocator(locator),
        visible: false,
        ...this.captureTextAnchor(),
      },
      questions: [
        mode === 'voice'
          ? { tempId: crypto.randomUUID(), text: seedText ?? '', choices: [], correctChoice: 0 }
          : makeEmptyDraftQuestion(),
      ],
      answerType: mode === 'question' ? 'multipleChoice' : undefined,
      pronunciationAssessmentEnabled: mode === 'voice' ? true : undefined,
    });
    this.aiSeedText.set(ai ? (seedText ?? '') : undefined);
    this.quizMode.set(mode);
  }

  /**
   * Starts a draft tied to a highlight of the selected text, as the previous NubeReader did for
   * materials created from a selection: the highlight being edited, or a new yellow one saved
   * first. Without selected text nor a highlight being edited it is a plain draft.
   */
  async startCreateQuizFromSelection(
    ai: boolean,
    selectedText: string | undefined,
    mode: QuizMode = 'quiz',
  ): Promise<void> {
    let highlight: Highlight | undefined = undefined;
    try {
      highlight = await this.selectionHighlight(selectedText);
    } catch (error) {
      this.toastService.error(
        String(this.translateService.instant('QUESTIONS.ERROR.SAVE_SELECTION')),
      );
      throw error;
    }
    this.startCreateQuiz(ai, highlight?.selectedText ?? selectedText, mode);
    if (!highlight) return;
    // Ties the draft to the highlight and anchors it to the highlight's quote: students do not
    // get the creator's highlights, so the quote must travel with the question itself.
    this.quizDraft.update((draft) =>
      draft ? { ...draft, selection: { ...draft.selection, id: highlight.id } } : draft,
    );
    this.anchorDraftTo(highlight.locator.text);
  }

  private async selectionHighlight(
    selectedText: string | undefined,
  ): Promise<Highlight | undefined> {
    const editingHighlight = this.readerNavigatorService.selectionEdit();
    if (editingHighlight) return editingHighlight;
    if (!selectedText) return undefined;
    const selection = await this.selectionsService.saveSelection({
      selectedText,
      color: 'highlight-yellow',
      isBookmark: false,
    });
    return selection && isHighlight(selection) ? selection : undefined;
  }

  // Re-anchors the draft to `quote` (a highlight's text, say) with the same policy as a fresh
  // draft; the live selection and the first text on the page are the fallbacks.
  anchorDraftTo(quote: Partial<TextQuote> | undefined): void {
    if (!this.quizDraft()) return;
    const anchor = this.verifiedAnchor(quote);
    this.quizDraft.update((draft) => {
      if (!draft) return draft;
      const selection = { ...draft.selection, ...anchor };
      if (!anchor.textAnchor) delete selection.textAnchor;
      return { ...draft, selection };
    });
  }

  private captureTextAnchor(): Pick<QuizSelection, 'textAnchor'> {
    return this.verifiedAnchor(undefined);
  }

  // The first of `preferred`, the live selection's quote and the first text on the page that
  // resolves on this very page (otherwise the stored progression does better). Fixed-layout pages
  // are placed whole, so they need no anchor.
  private verifiedAnchor(
    preferred: Partial<TextQuote> | undefined,
  ): Pick<QuizSelection, 'textAnchor'> {
    if (this.metadataService.isFixedLayout()) return {};
    const anchors = createAnchorLocator(this.readerNavigatorService.getCurrentFrameElements());
    const onPage = (quote: TextQuote | undefined): TextQuote | undefined =>
      quote && anchors.quote(quote) === 'onPage' ? quote : undefined;
    const selected = this.readerNavigatorService.currentSelectionContext();
    const candidate =
      onPage(QuizService.boundedQuote(preferred)) ??
      onPage(
        QuizService.boundedQuote(
          selected && {
            before: selected.before,
            highlight: selected.selectedText,
            after: selected.after,
          },
        ),
      ) ??
      onPage(anchors.firstQuoteOnPage());
    if (!candidate) return {};
    const { before = '', highlight, after = '' } = candidate;
    return { textAnchor: { before, highlight, after } };
  }

  // A long quote only needs its start to be found again: keep a bounded prefix (its `after`
  // context no longer follows it then). Whitespace is collapsed as in rendered text.
  private static boundedQuote(quote: Partial<TextQuote> | undefined): TextQuote | undefined {
    const highlight = quote?.highlight?.replace(/\s+/g, ' ').trim();
    if (!quote || !highlight) return undefined;
    if (highlight.length <= QuizService.MAX_ANCHOR_LENGTH) {
      return { before: quote.before, highlight, after: quote.after };
    }
    return {
      before: quote.before,
      highlight: highlight.slice(0, QuizService.MAX_ANCHOR_LENGTH).trimEnd(),
    };
  }

  startEditQuiz(quiz: QuizMaterialResponse): void {
    this.aiSeedText.set(undefined);
    const isStandaloneQuestion = quiz.materialKind === 'multi' || quiz.materialKind === 'simple';
    this.quizMode.set(quizModeFor(quiz.materialKind));
    this.quizDraft.set({
      id: quiz.id,
      name: quiz.name,
      relId: this.bookRelId(),
      selection: quiz.selection,
      time: quiz.time,
      applyTimeAllQuizzes: quiz.applyTimeAllQuizzes,
      maxAttempts: quiz.maxAttempts,
      randomQuestions: quiz.randomQuestions,
      numRandomQuestions: quiz.numRandomQuestions,
      competencies: quiz.competencies,
      questions: quiz.questions.map((question) => ({
        tempId: crypto.randomUUID(),
        id: question.id,
        text: question.text,
        choices: question.choices.map((choice) => ({ ...choice })),
        correctChoice: question.correctChoice,
        image: question.imageUrl ?? undefined,
      })),
      answerType:
        quiz.materialKind === 'simple'
          ? 'openEnded'
          : isStandaloneQuestion
            ? 'multipleChoice'
            : undefined,
      enableAiRating: quiz.materialKind === 'simple' ? Boolean(quiz.teacherAnswer) : undefined,
      referenceAnswer: quiz.materialKind === 'simple' ? quiz.teacherAnswer : undefined,
      embebbedContentType:
        quiz.materialKind === 'annotation' ? quiz.embebbedContentType : undefined,
      pronunciationAssessmentEnabled:
        quiz.materialKind === 'voice' ? quiz.pronunciationAssessmentEnabled : undefined,
      hasAnswers: quiz.materialKind === 'voice' ? quiz.hasAnswers : undefined,
      messyAnswers: quiz.materialKind === 'multi' ? quiz.messyAnswers : undefined,
    });
  }

  cancelQuizDraft(): void {
    this.quizDraft.set(undefined);
  }

  hasGenerationContent(source: AiTextSource, seedText: string): boolean {
    if (seedText.trim()) return true;
    return this.extractAiSourceText(source).trim().length > 0;
  }

  async generateQuizWithAi(
    options: GenerateQuizWithAiOptions,
  ): Promise<GenerateQuizWithAiResult | undefined> {
    const { source, seedText, questionsNumber, numberOfAnswers, competencyIds } = options;
    const inputText = (seedText.trim() || this.extractAiSourceText(source)).slice(
      0,
      MAX_AI_INPUT_LENGTH,
    );
    if (!inputText.trim()) return undefined;

    const competencies = this.metadataService
      .competencies()
      .filter((competency) => competencyIds.includes(competency._id));
    const customPrompts = competencies
      .map((competency) => competency.customAIPrompt)
      .filter((prompt): prompt is string => Boolean(prompt));

    try {
      const results = await this.readerApi.generateQuizQuestionsWithAi({
        inputText,
        questionsNumber,
        customPrompts: customPrompts.length ? customPrompts : undefined,
        language: this.metadataService.metadata()?.metadata.language,
      });
      if (!results.length) return undefined;
      return {
        questions: results.map((question) =>
          QuizService.normalizeAiQuestion(question, numberOfAnswers),
        ),
        partial: results.length < questionsNumber,
      };
    } catch (error) {
      debugLog('Error generating quiz with AI:', error);
      return undefined;
    }
  }

  validateImage(file: File): boolean {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      this.toastService.warning(
        String(this.translateService.instant('QUESTIONS.ERROR.IMAGE_FORMAT')),
      );
      return false;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      this.toastService.warning(
        String(this.translateService.instant('QUESTIONS.ERROR.IMAGE_SIZE')),
      );
      return false;
    }
    return true;
  }

  async saveQuizDraft(): Promise<void> {
    const draft = this.quizDraft();
    if (!draft) return;
    const mode = this.quizMode();
    try {
      const saved: RawMaterialResponse = await this.saveDraftForMode(mode, draft);
      this.upsertQuiz(saved);
      this.quizDraft.set(undefined);
      this.toastService.success(
        String(this.translateService.instant(QuizService.savedToastKey(mode))),
      );
    } catch (error) {
      debugLog('Error saving quiz:', error);
      this.toastService.error(
        String(this.translateService.instant(QuizService.saveErrorToastKey(mode))),
      );
    }
  }

  private async saveDraftForMode(mode: QuizMode, draft: QuizDraft): Promise<RawMaterialResponse> {
    if (mode === 'note') return this.saveAnnotation(draft);
    if (mode === 'voice') return this.saveVoiceQuestion(draft);
    if (mode === 'question') return this.saveStandaloneQuestion(draft);
    return this.saveQuiz(draft);
  }

  private static savedToastKey(mode: QuizMode): string {
    if (mode === 'note') return 'QUESTIONS.NOTE_SAVED';
    if (mode === 'voice') return 'QUESTIONS.VOICE_SAVED';
    if (mode === 'question') return 'QUESTIONS.QUESTION_SAVED';
    return 'QUESTIONS.QUIZ_SAVED';
  }

  private static saveErrorToastKey(mode: QuizMode): string {
    if (mode === 'note') return 'QUESTIONS.ERROR.SAVE_NOTE';
    if (mode === 'voice') return 'QUESTIONS.ERROR.SAVE_VOICE';
    if (mode === 'question') return 'QUESTIONS.ERROR.SAVE_QUESTION';
    return 'QUESTIONS.ERROR.SAVE_QUIZ';
  }

  private async saveAnnotation(draft: QuizDraft): Promise<AnnotationMaterialResponse> {
    const [question] = draft.questions;
    const payload: AnnotationMaterialSave = {
      id: draft.id,
      type: 'annotation',
      relId: draft.relId,
      selection: draft.selection,
      text: question.text,
      embebbedContentType: draft.embebbedContentType,
    };
    return draft.id
      ? this.readerApi.updateAnnotation(draft.id, payload)
      : this.readerApi.createAnnotation(payload);
  }

  private async saveVoiceQuestion(draft: QuizDraft): Promise<VoiceMaterialResponse> {
    const [question] = draft.questions;
    const payload: VoiceMaterialSave = {
      id: draft.id,
      type: 'voice',
      relId: draft.relId,
      selection: draft.selection,
      text: question.text,
      pronunciationAssessmentEnabled:
        this.metadataService.canUsePronunciationAssessment() &&
        Boolean(draft.pronunciationAssessmentEnabled),
      competencies: draft.competencies ?? [],
    };
    return draft.id
      ? this.readerApi.updateVoiceQuestion(draft.id, payload)
      : this.readerApi.createVoiceQuestion(payload);
  }

  private async saveQuiz(draft: QuizDraft): Promise<QuizMaterialResponse> {
    const payload: QuizMaterialSave = {
      id: draft.id,
      type: 'quiz',
      name: draft.name,
      relId: draft.relId,
      selection: draft.selection,
      time: QuizService.minutesToSeconds(draft.time),
      applyTimeAllQuizzes: draft.applyTimeAllQuizzes,
      maxAttempts: draft.maxAttempts,
      randomQuestions: draft.randomQuestions,
      numRandomQuestions:
        draft.numRandomQuestions === undefined
          ? undefined
          : Math.min(draft.numRandomQuestions, draft.questions.length),
      competencies: draft.competencies ?? [],
      questions: draft.questions.map((question) => ({
        id: question.id,
        text: question.text,
        choices: question.choices,
        correctChoice: question.correctChoice,
        imageUrl: question.imageRemoved ? null : question.image,
        hasImage: Boolean(question.image ?? question.newImage),
        imageChanged: question.imageRemoved,
        imageUUID: question.imageUUID,
      })),
    };
    const images = draft.questions
      .map((question) => question.newImage)
      .filter((file): file is File => Boolean(file));
    return draft.id
      ? this.readerApi.updateQuiz(draft.id, payload, images)
      : this.readerApi.createQuiz(payload, images);
  }

  private async saveStandaloneQuestion(draft: QuizDraft): Promise<QuestionMaterialResponse> {
    const [question] = draft.questions;
    const isOpenEnded = draft.answerType === 'openEnded';
    const imageFields = isOpenEnded
      ? {
          imageUrl: null,
          hasImage: false,
          imageChanged: Boolean(question.image) || question.imageRemoved,
          imageUUID: undefined,
        }
      : {
          imageUrl: question.imageRemoved ? null : question.image,
          hasImage: Boolean(question.image ?? question.newImage),
          imageChanged: question.imageRemoved,
          imageUUID: question.imageUUID,
        };
    const shared = {
      id: draft.id,
      text: question.text,
      relId: draft.relId,
      selection: draft.selection,
      time: QuizService.minutesToSeconds(draft.time),
      applyTimeAllQuizzes: draft.applyTimeAllQuizzes,
      maxAttempts: isOpenEnded ? undefined : draft.maxAttempts,
      competencies: draft.competencies ?? [],
      ...imageFields,
    };
    const payload: QuestionMaterialSave = isOpenEnded
      ? {
          ...shared,
          type: 'simple',
          teacherAnswer: draft.enableAiRating ? draft.referenceAnswer : undefined,
        }
      : {
          ...shared,
          type: 'multi',
          choices: question.choices,
          correctChoice: question.correctChoice,
          messyAnswers: draft.messyAnswers,
        };
    const images = !isOpenEnded && question.newImage ? [question.newImage] : [];
    return draft.id
      ? this.readerApi.updateQuestion(draft.id, payload, images)
      : this.readerApi.createQuestion(payload, images);
  }

  async deleteQuiz(quizId: string, relId: string): Promise<void> {
    const deleted = this.quizzes().find(({ quiz }) => quiz.id === quizId)?.quiz;
    const mode = deleted ? quizModeFor(deleted.materialKind) : this.quizMode();
    try {
      await this.readerApi.deleteQuiz(quizId, relId);
      this.rawQuizzes.update((quizzes) => quizzes.filter((quiz) => quiz.id !== quizId));
      if (this.quizDraft()?.id === quizId) this.quizDraft.set(undefined);
      this.toastService.success(
        String(this.translateService.instant(QuizService.deletedToastKey(mode))),
      );
    } catch (error) {
      debugLog('Error deleting quiz:', error);
      this.toastService.error(
        String(this.translateService.instant(QuizService.deleteErrorToastKey(mode))),
      );
    }
  }

  private static deletedToastKey(mode: QuizMode): string {
    if (mode === 'note') return 'QUESTIONS.DELETED_NOTE';
    if (mode === 'voice') return 'QUESTIONS.DELETED_VOICE';
    if (mode === 'question') return 'QUESTIONS.DELETED_QUESTION';
    return 'QUESTIONS.DELETED_QUIZ';
  }

  private static deleteErrorToastKey(mode: QuizMode): string {
    if (mode === 'note') return 'QUESTIONS.ERROR.DELETE_NOTE';
    if (mode === 'voice') return 'QUESTIONS.ERROR.DELETE_VOICE';
    if (mode === 'question') return 'QUESTIONS.ERROR.DELETE_QUESTION';
    return 'QUESTIONS.ERROR.DELETE_QUIZ';
  }

  startAnswerQuiz(quiz: QuizMaterialResponse): void {
    this.quizToAnswer.set(quiz);
  }

  closeQuizAnswer(): void {
    this.quizToAnswer.set(undefined);
  }

  async submitQuizAnswer(
    quiz: QuizMaterialResponse,
    options: { values: QuizAnswerValueSave[]; existingAnswerId?: string; attempt?: number },
  ): Promise<QuizAnswerResponse | undefined> {
    const { values, existingAnswerId, attempt } = options;
    const payload: QuizAnswerSave = {
      id: existingAnswerId,
      quiz: quiz.id,
      type: 'quiz',
      userName: this.metadataService.metadata()?.userConfig?.userName ?? '',
      relId: this.bookRelId(),
      attempt,
      values,
      answeredTime: new Date(),
    };
    try {
      const answer = await this.readerApi.saveQuizAnswer(payload);
      this.upsertAnswer(answer);
      return answer;
    } catch (error) {
      debugLog('Error saving quiz answer:', error);
      this.toastService.error(String(this.translateService.instant('QUESTIONS.ERROR.ANSWER')));
      return undefined;
    }
  }

  async submitQuestionAnswer(
    quiz: QuizMaterialResponse,
    options: { value: string; existingAnswerId?: string; attempt?: number },
  ): Promise<QuizAnswerResponse | undefined> {
    const { value, existingAnswerId, attempt } = options;
    const payload: QuestionAnswerSave = {
      id: existingAnswerId,
      question: quiz.id,
      type: quiz.materialKind === 'simple' ? 'simple' : 'multi',
      userName: this.metadataService.metadata()?.userConfig?.userName ?? '',
      relId: this.bookRelId(),
      attempt,
      value,
      answeredTime: new Date(),
    };
    try {
      const raw = await this.readerApi.saveQuestionAnswer(payload);
      this.upsertAnswer(raw);
      return QuizService.normalizeAnswer(raw);
    } catch (error) {
      debugLog('Error saving question answer:', error);
      this.toastService.error(String(this.translateService.instant('QUESTIONS.ERROR.ANSWER')));
      return undefined;
    }
  }

  async submitVoiceAnswer(
    quiz: QuizMaterialResponse,
    audio: Blob,
    durationMs: number,
  ): Promise<QuizAnswerResponse | undefined> {
    const payload: VoiceAnswerSave = {
      question: quiz.id,
      type: 'voice',
      relId: this.bookRelId(),
      userName: this.metadataService.metadata()?.userConfig?.userName ?? '',
      audioDurationMs: durationMs,
      language: this.metadataService.metadata()?.metadata.language,
      answeredTime: new Date(),
    };
    try {
      const raw = await this.readerApi.saveVoiceAnswer(payload, audio);
      this.upsertAnswer(raw);
      return QuizService.normalizeAnswer(raw);
    } catch (error) {
      debugLog('Error saving voice answer:', error);
      this.toastService.error(String(this.translateService.instant('QUESTIONS.ERROR.ANSWER')));
      return undefined;
    }
  }

  async finishQuizAttempt(answerId: string): Promise<QuizAnswerResponse | undefined> {
    try {
      const answer = await this.readerApi.finishQuizAnswer(answerId, this.bookRelId());
      this.upsertAnswer(answer);
      return answer;
    } catch (error) {
      debugLog('Error finishing quiz attempt:', error);
      this.toastService.error(String(this.translateService.instant('QUESTIONS.ERROR.ANSWER')));
      return undefined;
    }
  }

  private upsertQuiz(quiz: RawMaterialResponse): void {
    this.rawQuizzes.update((quizzes) => QuizService.upsertById(quizzes, quiz));
  }

  private upsertAnswer(answer: RawAnswerResponse): void {
    this.rawAnswers.update((answers) => QuizService.upsertById(answers, answer));
  }

  private static upsertById<T extends { id: string }>(items: T[], item: T): T[] {
    return items.some((existing) => existing.id === item.id)
      ? items.map((existing) => (existing.id === item.id ? item : existing))
      : [...items, item];
  }

  private extractAiSourceText(source: AiTextSource): string {
    const frames = this.readerNavigatorService.getCurrentFrameElements();
    if (source === 'visible') return getVisibleTextFragment(frames);
    const body = findVisibleIframe(frames)?.contentDocument?.body;
    return body ? getBodyTextForSpeech(body) : '';
  }

  private static normalizeAiQuestion(
    question: QuizQuestionAiResponse,
    numberOfAnswers: number,
  ): QuizDraftQuestion {
    let choices: QuizChoice[] = question.choices.map((text) => ({ text }));
    let { correctChoice } = question;

    if (choices.length > numberOfAnswers) {
      if (correctChoice >= numberOfAnswers) {
        choices[numberOfAnswers - 1] = choices[correctChoice];
        correctChoice = numberOfAnswers - 1;
      }
      choices = choices.slice(0, numberOfAnswers);
    } else if (choices.length < numberOfAnswers) {
      const padding = Array.from({ length: numberOfAnswers - choices.length }, () => ({
        text: '',
      }));
      choices = [...choices, ...padding];
    }

    return { tempId: crypto.randomUUID(), text: question.text, choices, correctChoice };
  }

  private enrich(raw: RawMaterialResponse): QuizWithStatus {
    const quiz = QuizService.normalizeMaterial(raw);
    const answersForQuiz = this.rawAnswers()
      .filter((answer) => ('quiz' in answer ? answer.quiz : answer.question) === quiz.id)
      .map((answer) => QuizService.normalizeAnswer(answer))
      .sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime());
    const [myAnswer] = answersForQuiz;
    const isLocked = answersForQuiz.length > 0;
    return {
      quiz,
      myAnswer,
      isLocked,
      isPending: !isLocked || (Boolean(myAnswer.attempt) && !myAnswer.finished),
    };
  }

  private static secondsToMinutes(seconds: number | undefined): number | undefined {
    return seconds === undefined ? undefined : seconds / SECONDS_PER_MINUTE;
  }

  private static minutesToSeconds(minutes: number | undefined): number | undefined {
    return minutes === undefined ? undefined : minutes * SECONDS_PER_MINUTE;
  }

  private static normalizeMaterial(material: RawMaterialResponse): QuizMaterialResponse {
    if (material.type === 'quiz') {
      return {
        ...material,
        materialKind: 'quiz',
        time: QuizService.secondsToMinutes(material.time),
        questions: material.questions.map((question) => ({
          ...question,
          choiceOrder: QuizService.choiceOrder(question.newShuffledOrder, question.choices.length),
        })),
      };
    }
    const shared = {
      id: material.id,
      type: 'quiz' as const,
      name: material.text,
      relId: material.relId,
      parentId: material.parentId,
      selection: material.selection,
      created: material.created,
      migrated: material.migrated,
    };
    const withoutAnswer = {
      ...shared,
      questions: [{ id: material.id, text: material.text, choices: [], correctChoice: -1 }],
    };
    if (material.type === 'annotation') {
      return {
        ...withoutAnswer,
        materialKind: 'annotation',
        embebbedContentType: material.embebbedContentType,
      };
    }
    if (material.type === 'voice') {
      return {
        ...withoutAnswer,
        materialKind: 'voice',
        pronunciationAssessmentEnabled: material.pronunciationAssessmentEnabled,
        hasAnswers: material.hasAnswers,
        competencies: material.competencies,
      };
    }
    const withAttemptFields = {
      ...shared,
      time: QuizService.secondsToMinutes(material.time),
      applyTimeAllQuizzes: material.applyTimeAllQuizzes,
      maxAttempts: material.maxAttempts,
      competencies: material.competencies,
    };
    return material.type === 'multi'
      ? {
          ...withAttemptFields,
          materialKind: 'multi',
          hasAnswers: material.hasAnswers,
          messyAnswers: material.messyAnswers,
          questions: [
            {
              id: material.id,
              text: material.text,
              choices: material.choices,
              correctChoice: material.correctChoice,
              choiceOrder: QuizService.choiceOrder(
                material.newShuffledOrder,
                material.choices.length,
              ),
              imageUrl: material.imageUrl,
              hasImage: material.hasImage,
            },
          ],
        }
      : {
          ...withAttemptFields,
          materialKind: 'simple',
          teacherAnswer: material.teacherAnswer,
          hasAnswers: material.hasAnswers,
          questions: [
            {
              id: material.id,
              text: material.text,
              choices: [],
              correctChoice: -1,
              imageUrl: material.imageUrl,
              hasImage: material.hasImage,
            },
          ],
        };
  }

  /**
   * Inverts the backend's `newShuffledOrder` (original index → shown position) into the original
   * index of each shown choice. Undefined when the choices are not shuffled or the order does not
   * match them, so they are then taken as they come.
   */
  private static choiceOrder(
    newShuffledOrder: string | undefined,
    choiceCount: number,
  ): number[] | undefined {
    if (!newShuffledOrder) return undefined;
    const shownPositions = newShuffledOrder.split('-').map(Number);
    const isPermutation =
      shownPositions.length === choiceCount &&
      new Set(shownPositions).size === choiceCount &&
      shownPositions.every(
        (shownPosition) =>
          Number.isInteger(shownPosition) && shownPosition >= 0 && shownPosition < choiceCount,
      );
    if (!isPermutation) return undefined;
    const order: number[] = [];
    shownPositions.forEach((shownPosition, originalIndex) => {
      order[shownPosition] = originalIndex;
    });
    return order;
  }

  private static normalizeAnswer(raw: RawAnswerResponse): QuizAnswerResponse {
    if ('quiz' in raw) return raw;
    if (raw.type === 'voice') {
      return {
        id: raw.id,
        quiz: raw.question,
        type: 'voice',
        userId: raw.userId,
        userName: raw.userName,
        relId: raw.relId,
        values: [],
        score: raw.score,
        pendingManualReview: raw.pendingManualReview,
        manualReviewReason: raw.manualReviewReason,
        pronunciationAssessment: raw.pronunciationAssessment,
        audioUrl: raw.audioURL,
        audioDurationMs: raw.audioDurationMs,
        created: raw.created,
        updated: raw.updated,
        pendingSync: raw.pendingSync,
      };
    }
    const base = {
      id: raw.id,
      quiz: raw.question,
      userId: raw.userId,
      userName: raw.userName,
      relId: raw.relId,
      attempt: raw.attempt,
      finished: raw.finished,
      sent: raw.sent,
      score: raw.score,
      pendingSync: raw.pendingSync,
      created: raw.created,
      updated: raw.updated,
    };
    return raw.type === 'multi'
      ? {
          ...base,
          type: 'multi',
          values: [
            {
              questionId: raw.question,
              value: Number(raw.value),
              correctChoice: raw.correctChoice,
            },
          ],
        }
      : { ...base, type: 'simple', values: [], openEndedValue: String(raw.value) };
  }
}
